import { useEffect } from 'react';
import { useStore } from '../../store';
import { useIsMobile } from '../useIsMobile';

type ChatHistoryState = { chat?: boolean; chatLightbox?: boolean } | null;

/** Module-level, so a StrictMode re-mount cancels the pop its cleanup queued. */
let pendingChatBack: ReturnType<typeof setTimeout> | undefined;

/**
 * On the phone the panel covers the screen, so the back button must close it
 * rather than leave the page. One pushed entry per open; popstate closes.
 */
export function useMobileBackClose(): void {
  const isMobile = useIsMobile();
  const isChatOpen = useStore(state => state.isChatOpen);
  const closeChat = useStore(state => state.closeChat);

  useEffect(() => {
    if (!isMobile || !isChatOpen) return;
    clearTimeout(pendingChatBack);
    // A StrictMode re-run finds its own entry on top and keeps it.
    if (!(window.history.state as ChatHistoryState)?.chat)
      window.history.pushState({ chat: true }, '');
    const onPopState = (event: PopStateEvent) => {
      const state = event.state as ChatHistoryState;
      // Back from a layer above the panel (the attachment lightbox) lands on
      // the panel's own entry: that layer closed, the chat stays (EMR-1878).
      // A traversal that lands on a lightbox entry (Forward) is not a Back out
      // of the panel either.
      if (state?.chat || state?.chatLightbox) return;
      closeChat();
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      // Closed by a control, not by Back: remove the panel's entry, or the next
      // Back after a reopen lands on it and is ignored. Deferred, so a
      // StrictMode re-mount can cancel it.
      pendingChatBack = setTimeout(() => {
        if ((window.history.state as ChatHistoryState)?.chat) {
          window.history.back();
        }
      });
    };
  }, [isMobile, isChatOpen, closeChat]);
}
